/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import {
  getWeeksInMonth,
  getDatesInWeek,
  getStartOfWeek,
  getDaysOfWeekNames,
  WeekInMonthIndex
} from '#utils/PRIVATE_dayUtils';
import { ComponentProps } from 'preact/compat';
import {
  CalendarDateRequired,
  formatFullCalendarDate,
  isDateOutOfRange
} from '#utils/UNSAFE_calendarDateUtils';
import { DayCell } from './DayCell';
import { useUser } from '#hooks/UNSAFE_useUser';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { Grid } from '#UNSAFE_Grid';
import { MonthViewVariantOptions } from '#PRIVATE_MonthView/themes/MonthViewStyles.css';
import { MonthViewRedwoodTheme } from '#PRIVATE_MonthView/themes/redwood/MonthViewTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { monthViewScaleVars } from '#PRIVATE_MonthView/themes/MonthViewContract.css';

type PickedDayCellProps = Pick<
  ComponentProps<typeof DayCell>,
  'dayFormatter' | 'onAction' | 'onNavigation'
>;

type MonthViewProps = TestIdProps &
  PickedDayCellProps & {
    /**
     * The daysOutsideMonth property affects the day cells that are outside of the current month.
     * If set to 'hidden', then the days outside of the current month will be hidden.
     * If set to 'selectable', then the days outside of the current month will be visible and selectable.
     * This defaults to 'hidden'.
     */
    daysOutsideMonth?: 'hidden' | 'selectable';
    /**
     * The MonthView renders with the month of the focusableDate.
     * The focusableDate is the only day in the MonthView that has tabIndex: 0 on it.
     * The focusableDate doesn't have focus until the user tabs in to the Month View.
     * The focusableDate is never undefined.
     */
    focusableDate: CalendarDateRequired;
    /**
     * If set to true, the focusableDate will be focused and you will see the focus ring. It will be
     * false when no date has focus.
     * @default false
     */
    isFocused?: boolean;
    /**
     * Specifies whether the component is readonly.
     */
    isReadonly?: boolean;
    /**
     * The maximum selectable date. The max must be greater than the min if they are both specified.
     */
    max?: CalendarDateRequired;
    /**
     * The minimum selectable date. The min must be less than the max if they are both specified.
     */
    min?: CalendarDateRequired;
    /**
     * The selected date of the MonthView, or undefined is there is no selected date. This
     * is used to highlight the selected day cell if it is within the min/max range.
     */
    selectedDate?: CalendarDateRequired;
    /**
     * Today's date. This is used to highlight the Today day cell.
     */
    todaysDate: CalendarDateRequired;
  };

/**
 * A MonthView is a month interface that allows users to select a single date. The MonthView is the week day header
 * abbreviations and the days of the month. A MonthView is used inside of a DatePicker
 * along with the DatePickerHeader and the MonthGridView and YearGridView.
 */
export const MonthView = ({
  dayFormatter,
  daysOutsideMonth = 'hidden',
  isReadonly = false,
  max,
  min,
  onAction,
  onNavigation,
  selectedDate,
  focusableDate,
  isFocused = false,
  testId,
  todaysDate
}: MonthViewProps) => {
  const { locale } = useUser();
  const weeksInMonth = getWeeksInMonth(focusableDate.year, focusableDate.month, locale);
  const { baseTheme, styles } = useComponentTheme<MonthViewVariantOptions>(
    MonthViewRedwoodTheme,
    {}
  );

  const renderMonthViewHeaderRow = () => {
    const weekDays = getDaysOfWeekNames(locale, 'short');
    return (
      <Grid
        role="row"
        aria-hidden="true"
        gridTemplateColumns="repeat(7, 1fr)"
        gap={monthViewScaleVars.columnGap}>
        {weekDays.map((day) => (
          <div role="columnheader" class={styles['doyOfTheWeek']}>
            <span>{day}</span>
          </div>
        ))}
      </Grid>
    );
  };

  const renderMonthView = () => {
    const calendarGrid: JSX.Element[] = [];

    for (let weekIndex = 0; weekIndex < weeksInMonth; weekIndex++) {
      const startDateOfWeek = getStartOfWeek(focusableDate.year, focusableDate.month, 1, locale);
      const weekDates = getDatesInWeek(
        weekIndex as WeekInMonthIndex,
        startDateOfWeek.year,
        startDateOfWeek.month,
        startDateOfWeek.day
      );

      const weekCells = weekDates.map((dayCellDate) => {
        // This figures out the visual state of the DayCell.
        const isDisabled = isDateOutOfRange(dayCellDate, min, max);

        // Even if the selected date is out of range, the UX design shows it as selected.
        const isSelected = selectedDate ? isSameDate(dayCellDate, selectedDate) : false;
        const isToday = isSameDate(dayCellDate, todaysDate);
        const isFocusable = isSameDate(dayCellDate, focusableDate);
        const isAdjacentMonth = isOutOfMonth(dayCellDate, focusableDate);
        const isHidden = isAdjacentMonth && daysOutsideMonth === 'hidden';

        return (
          <DayCell
            key={`${dayCellDate.year}-${dayCellDate.month}-${dayCellDate.day}`}
            date={dayCellDate}
            dayFormatter={dayFormatter}
            isAdjacentMonth={isAdjacentMonth}
            isDisabled={isDisabled}
            isFocusable={isFocusable}
            isFocused={isFocusable && isFocused}
            isHidden={isHidden}
            isReadonly={isReadonly}
            isSelected={isSelected}
            isToday={isToday}
            onAction={onAction}
            onNavigation={onNavigation}></DayCell>
        );
      });

      calendarGrid.push(
        <Grid
          key={weekIndex}
          role="row"
          gridTemplateColumns="repeat(7, 1fr)"
          gap={monthViewScaleVars.columnGap}>
          {weekCells}
        </Grid>
      );
    }

    return calendarGrid;
  };

  const monthViewAriaLabel = formatFullCalendarDate(locale, focusableDate, 'month');
  return (
    <div class={baseTheme}>
      <Grid
        gap={monthViewScaleVars.rowGap}
        aria-label={monthViewAriaLabel}
        role="grid"
        testId={testId}>
        {renderMonthViewHeaderRow()}
        {renderMonthView()}
      </Grid>
    </div>
  );
};

function isSameDate(date1: CalendarDateRequired, date2: CalendarDateRequired) {
  return date1.year === date2.year && date1.month === date2.month && date1.day === date2.day;
}

function isOutOfMonth(date1: CalendarDateRequired, currentMonthDate: CalendarDateRequired) {
  return date1.month !== currentMonthDate.month;
}
